import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'

// Invite links always point at the production web app, same as getShareUrl:
// on native, window.location.origin is a Capacitor-internal scheme nobody else
// can open.
const PROD_APP_HOST = 'app.getdub.com'

export function getGroupInviteUrl(groupId) {
  return `https://${PROD_APP_HOST}/join/${groupId}`
}

// A visitor who opens an invite while signed out has to sign up, confirm their
// email and log in before they can join. The intended-URL store expires after
// an hour, which an email confirmation can outlast, so the invite gets its own
// key with a week's lifetime. Login reads it and sends them back to /join.
const KEY = 'dub_pending_group_invite'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

export function savePendingGroupInvite(groupId) {
  if (!groupId) return
  try {
    localStorage.setItem(KEY, JSON.stringify({ groupId, savedAt: Date.now() }))
  } catch {
    // localStorage unavailable (private mode, quota) — best-effort, ignore.
  }
}

export function readPendingGroupInvite() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.groupId || typeof parsed.savedAt !== 'number'
        || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(KEY)
      return null
    }
    return parsed.groupId
  } catch {
    try { localStorage.removeItem(KEY) } catch { /* ignore */ }
    return null
  }
}

export function clearPendingGroupInvite() {
  try { localStorage.removeItem(KEY) } catch { /* ignore */ }
}

// True where a share sheet is the natural way to send a link: the native app
// and phone browsers. Desktop Safari and Edge also expose navigator.share,
// but on a computer a copied link is the better fit.
export function canUseShareSheet() {
  if (Capacitor.isNativePlatform()) return true
  return typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && typeof window !== 'undefined'
    && !!window.matchMedia?.('(pointer: coarse)').matches
}

// Opens the device share sheet. Resolves to 'shared' or 'cancelled'; any
// other failure throws so the caller can fall back to the copy dialog.
export async function shareGroupInvite({ groupId, groupName }) {
  const url = getGroupInviteUrl(groupId)
  const title = `Join ${groupName} on dub`
  const text = `Join my group "${groupName}" on dub and make your bracket picks with us.`
  try {
    if (Capacitor.isNativePlatform()) {
      await Share.share({ title, text, url, dialogTitle: 'Invite to group' })
    } else {
      await navigator.share({ title, text, url })
    }
    return 'shared'
  } catch (err) {
    if (err?.name === 'AbortError' || /cancel/i.test(err?.message || '')) return 'cancelled'
    throw err
  }
}
